import React, { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import * as THREE from "three";

// Use React.createElement (h) for r3f primitives to bypass the visual-edits
// babel plugin which injects "x-line-number"/etc. attributes that break
// three.js applyProps.
const h = React.createElement;

// Low-poly devil head (red horns + face)
function DevilHead({ position = [0, 0, 0], scale = 1 }) {
    const ref = useRef();
    useFrame((s) => {
        if (ref.current) ref.current.rotation.y = s.clock.elapsedTime * 0.4;
    });
    return h(
        "group",
        { ref, position, scale },
        // face
        h(
            "mesh",
            null,
            h("sphereGeometry", { args: [0.55, 24, 24] }),
            h("meshStandardMaterial", {
                color: "#ef4444",
                emissive: "#ef4444",
                emissiveIntensity: 0.35,
                roughness: 0.5,
            })
        ),
        // left horn
        h(
            "mesh",
            { position: [-0.35, 0.45, 0], rotation: [0, 0, -0.5] },
            h("coneGeometry", { args: [0.16, 0.55, 14] }),
            h("meshStandardMaterial", {
                color: "#7f1d1d",
                emissive: "#dc2626",
                emissiveIntensity: 0.25,
            })
        ),
        // right horn
        h(
            "mesh",
            { position: [0.35, 0.45, 0], rotation: [0, 0, 0.5] },
            h("coneGeometry", { args: [0.16, 0.55, 14] }),
            h("meshStandardMaterial", {
                color: "#7f1d1d",
                emissive: "#dc2626",
                emissiveIntensity: 0.25,
            })
        ),
        // eyes
        h(
            "mesh",
            { position: [-0.18, 0.05, 0.45] },
            h("sphereGeometry", { args: [0.08, 12, 12] }),
            h("meshStandardMaterial", {
                color: "#fde047",
                emissive: "#fde047",
                emissiveIntensity: 0.9,
            })
        ),
        h(
            "mesh",
            { position: [0.18, 0.05, 0.45] },
            h("sphereGeometry", { args: [0.08, 12, 12] }),
            h("meshStandardMaterial", {
                color: "#fde047",
                emissive: "#fde047",
                emissiveIntensity: 0.9,
            })
        )
    );
}

// Low-poly goblin head (green ears + face)
function GoblinHead({ position = [0, 0, 0], scale = 1 }) {
    const ref = useRef();
    useFrame((s) => {
        if (ref.current) ref.current.rotation.y = -s.clock.elapsedTime * 0.35;
    });
    return h(
        "group",
        { ref, position, scale },
        h(
            "mesh",
            null,
            h("sphereGeometry", { args: [0.55, 24, 24] }),
            h("meshStandardMaterial", {
                color: "#22c55e",
                emissive: "#22c55e",
                emissiveIntensity: 0.3,
                roughness: 0.55,
            })
        ),
        h(
            "mesh",
            { position: [-0.55, 0.1, 0], rotation: [0, 0, -0.7] },
            h("coneGeometry", { args: [0.18, 0.5, 14] }),
            h("meshStandardMaterial", {
                color: "#15803d",
                emissive: "#22c55e",
                emissiveIntensity: 0.2,
            })
        ),
        h(
            "mesh",
            { position: [0.55, 0.1, 0], rotation: [0, 0, 0.7] },
            h("coneGeometry", { args: [0.18, 0.5, 14] }),
            h("meshStandardMaterial", {
                color: "#15803d",
                emissive: "#22c55e",
                emissiveIntensity: 0.2,
            })
        ),
        h(
            "mesh",
            { position: [-0.16, 0.06, 0.46] },
            h("sphereGeometry", { args: [0.07, 12, 12] }),
            h("meshStandardMaterial", { color: "#0a0a0f" })
        ),
        h(
            "mesh",
            { position: [0.16, 0.06, 0.46] },
            h("sphereGeometry", { args: [0.07, 12, 12] }),
            h("meshStandardMaterial", { color: "#0a0a0f" })
        )
    );
}

// Floating arrow (cylinder + cone tip)
function Arrow({ position = [0, 0, 0], rotation = [0, 0, 0], color = "#a855f7" }) {
    const ref = useRef();
    useFrame((s, dt) => {
        if (ref.current) ref.current.rotation.z += dt * 0.3;
    });
    return h(
        "group",
        { ref, position, rotation },
        h(
            "mesh",
            null,
            h("cylinderGeometry", { args: [0.04, 0.04, 0.9, 12] }),
            h("meshStandardMaterial", {
                color,
                emissive: color,
                emissiveIntensity: 0.45,
            })
        ),
        h(
            "mesh",
            { position: [0, 0.55, 0] },
            h("coneGeometry", { args: [0.13, 0.25, 14] }),
            h("meshStandardMaterial", {
                color: "#ffffff",
                emissive: color,
                emissiveIntensity: 0.55,
            })
        )
    );
}

function SceneContent() {
    const arrows = useMemo(
        () => [
            { p: [-2.6, 1.2, -1], r: [0, 0, 0.4], c: "#a855f7" },
            { p: [2.7, -1.0, -1.2], r: [0, 0, -0.6], c: "#22c55e" },
            { p: [-3.2, -1.4, -0.5], r: [0, 0, -0.3], c: "#6f3fec" },
            { p: [3.0, 1.4, -0.3], r: [0, 0, 0.5], c: "#ef4444" },
            { p: [0, 2.2, -2], r: [0, 0, 0.0], c: "#f97316" },
        ],
        []
    );
    return h(
        React.Fragment,
        null,
        h("fog", { attach: "fog", args: ["#0a0a0f", 4, 14] }),
        h("ambientLight", { intensity: 0.4 }),
        h("pointLight", { color: "#6f3fec", intensity: 1.6, distance: 14, position: [3, 3, 3] }),
        h("pointLight", { color: "#22c55e", intensity: 1.0, distance: 12, position: [-3, -2, 2] }),
        h("pointLight", { color: "#ef4444", intensity: 0.8, distance: 10, position: [2, -3, 1] }),
        h(
            Float,
            { speed: 1.2, rotationIntensity: 0.5, floatIntensity: 0.9 },
            h(DevilHead, { position: [-2.0, 0.4, 0], scale: 1.0 })
        ),
        h(
            Float,
            { speed: 1.0, rotationIntensity: 0.5, floatIntensity: 0.9 },
            h(GoblinHead, { position: [2.1, -0.3, -0.5], scale: 1.0 })
        ),
        h(
            Float,
            { speed: 1.4, rotationIntensity: 0.3, floatIntensity: 1.2 },
            h(DevilHead, { position: [1.6, 1.7, -1.5], scale: 0.55 })
        ),
        h(
            Float,
            { speed: 1.3, rotationIntensity: 0.3, floatIntensity: 1.0 },
            h(GoblinHead, { position: [-1.8, -1.6, -1.2], scale: 0.55 })
        ),
        ...arrows.map((a, i) =>
            h(
                Float,
                { key: i, speed: 1 + i * 0.2, rotationIntensity: 0.4, floatIntensity: 1.2 },
                h(Arrow, { position: a.p, rotation: a.r, color: a.c })
            )
        )
    );
}

export const Scene3D = ({ className = "" }) => {
    const reducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
        return <div className={`absolute inset-0 pp-bg-0 ${className}`} aria-hidden="true" />;
    }
    return (
        <div
            className={`absolute inset-0 ${className}`}
            aria-hidden="true"
            data-testid="scene-3d"
        >
            <Canvas
                dpr={[1, 1.5]}
                camera={{ fov: 60, position: [0, 0.2, 6.0] }}
                gl={{ powerPreference: "low-power", antialias: true, alpha: true }}
            >
                {h("color", { attach: "background", args: [new THREE.Color("#0a0a0f")] })}
                <Suspense fallback={null}>
                    <SceneContent />
                </Suspense>
            </Canvas>
        </div>
    );
};
